import { useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Heart, Signature, Phone, Landmark } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { renderAnswerBlocks } from "../lib/safeHtml";
import { HTTP_PAYMENT_REQUIRED } from "../lib/httpStatus";

const Loader = () => (
  <div className="flex flex-col items-center py-12 gap-6">
    <svg className="glyph-loader" width="64" height="64" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="26" stroke="#D4AF37" strokeWidth="1" strokeDasharray="4 6" />
      <circle cx="32" cy="32" r="16" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
      <path d="M32 8 L40 32 L32 56 L24 32 Z" stroke="#D4AF37" strokeWidth="1" fill="none" />
      <circle cx="32" cy="32" r="3" fill="#D4AF37" />
    </svg>
    <p className="text-sm uppercase tracking-[0.3em] text-neutral-400">Consulting the numbers…</p>
  </div>
);

const renderAnswer = (text) =>
  renderAnswerBlocks(text).map(({ key, __html }) => (
    <p key={key} dangerouslySetInnerHTML={{ __html }} />
  ));

const ResultCard = ({ score, verdict, children, testid }) => (
  <div data-testid={testid} className="mt-6 sm:mt-8 reading-parchment rounded-2xl border border-gold/20 bg-[#141419] p-5 sm:p-8 lg:p-10 shadow-gold fade-up">
    <div className="relative">
      {(score !== undefined || verdict) && (
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          {score !== undefined && (
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-3xl sm:text-4xl text-gold">{score}</span>
              <span className="text-xs uppercase tracking-[0.25em] text-neutral-500">/ 100</span>
            </div>
          )}
          {verdict && (
            <span className="ml-auto text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-3 py-1">
              {verdict}
            </span>
          )}
        </div>
      )}
      <div className="reading-content">{children}</div>
    </div>
  </div>
);

/* ---------- Compatibility ---------- */
const CompatibilityForm = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name_a: "", dob_a: "", name_b: "", dob_b: "", relation: "partner" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const canSubmit = f.name_a && f.dob_a && f.name_b && f.dob_b;

  const submit = async () => {
    if (!canSubmit) { toast.error("Please fill all fields for both people."); return; }
    if ((user?.credits || 0) < 3) { toast.error("Compatibility costs 3 credits."); nav("/pricing"); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/numerology/compatibility", f);
      setRes(data);
      await refresh();
      toast.success("Compatibility revealed · 3 credits used");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
      if (e?.response?.status === HTTP_PAYMENT_REQUIRED) nav("/pricing");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="grid gap-5 sm:gap-8 md:grid-cols-2">
        {["a", "b"].map((k) => (
          <div key={k} className="rounded-2xl border border-white/5 bg-black/30 p-5 sm:p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Person {k.toUpperCase()}</p>
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Full name <span className="text-gold">*</span></Label>
            <Input
              data-testid={`compat-name-${k}`}
              value={f[`name_${k}`]}
              onChange={(e) => setF({ ...f, [`name_${k}`]: e.target.value })}
              className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold"
              placeholder={k === "a" ? "Amara Blackwood" : "Leo Vance"}
            />
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-4 block">Date of birth <span className="text-gold">*</span></Label>
            <Input
              data-testid={`compat-dob-${k}`}
              type="date"
              value={f[`dob_${k}`]}
              onChange={(e) => setF({ ...f, [`dob_${k}`]: e.target.value })}
              max={new Date().toISOString().slice(0, 10)}
              className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold text-neutral-200"
            />
          </div>
        ))}
      </div>

      <div className="mt-6">
        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Relationship type</Label>
        <div
          data-testid="compat-relation"
          role="radiogroup"
          className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:max-w-md"
        >
          {[
            { key: "partner", label: "Partner", hint: "Romantic bond" },
            { key: "friend", label: "Friend", hint: "Trusted circle" },
            { key: "business", label: "Business", hint: "Work / venture" },
          ].map((r) => {
            const active = f.relation === r.key;
            return (
              <button
                key={r.key}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`compat-relation-${r.key}`}
                onClick={() => setF({ ...f, relation: r.key })}
                className={`text-left rounded-2xl border px-4 py-3 transition-colors duration-200 ${
                  active
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-white/10 text-neutral-300 hover:border-white/25 hover:text-white"
                }`}
              >
                <div className="text-sm font-medium">{r.label}</div>
                <div className={`text-xs mt-0.5 ${active ? "text-gold/70" : "text-neutral-500"}`}>{r.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-neutral-400">Cost: <span className="text-gold">3 credits</span></span>
        <Button
          data-testid="compat-submit"
          onClick={submit}
          disabled={loading || !canSubmit}
          className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-8 transition-colors duration-200 disabled:opacity-50"
        >
          <Heart className="h-4 w-4 mr-2" /> {loading ? "Reading…" : "Reveal harmony"}
        </Button>
      </div>

      {loading && <Loader />}
      {res && (
        <ResultCard score={res.score} verdict={res.verdict} testid="compat-result">
          {renderAnswer(res.reading.answer)}
        </ResultCard>
      )}
    </div>
  );
};

/* ---------- Name Advisor ---------- */
const NameAdvisorForm = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current_name: "", dob: "", goal: "career and prosperity" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const canSubmit = f.current_name && f.dob;

  const submit = async () => {
    if (!canSubmit) { toast.error("Enter your name and date of birth."); return; }
    if ((user?.credits || 0) < 2) { toast.error("Name advisor costs 2 credits."); nav("/pricing"); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/numerology/name-suggest", f);
      setRes(data);
      await refresh();
      toast.success("Advisor spoke · 2 credits used");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
      if (e?.response?.status === HTTP_PAYMENT_REQUIRED) nav("/pricing");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Current name <span className="text-gold">*</span></Label>
          <Input
            data-testid="name-current"
            value={f.current_name}
            onChange={(e) => setF({ ...f, current_name: e.target.value })}
            placeholder="e.g. Amara Blackwood"
            className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Date of birth <span className="text-gold">*</span></Label>
          <Input
            data-testid="name-dob"
            type="date"
            value={f.dob}
            onChange={(e) => setF({ ...f, dob: e.target.value })}
            max={new Date().toISOString().slice(0, 10)}
            className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold text-neutral-200"
          />
        </div>
      </div>
      <div className="mt-5">
        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">What are you optimising for?</Label>
        <Textarea
          data-testid="name-goal"
          value={f.goal}
          onChange={(e) => setF({ ...f, goal: e.target.value })}
          className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold min-h-[80px]"
          placeholder="Career, love, business, creativity…"
        />
      </div>

      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-neutral-400">Cost: <span className="text-gold">2 credits</span></span>
        <Button
          data-testid="name-submit"
          onClick={submit}
          disabled={loading || !canSubmit}
          className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-8 transition-colors duration-200 disabled:opacity-50"
        >
          <Signature className="h-4 w-4 mr-2" /> {loading ? "Advising…" : "Suggest spellings"}
        </Button>
      </div>

      {loading && <Loader />}
      {res && (
        <ResultCard testid="name-result">
          {renderAnswer(res.reading.answer)}
          {res.shortlist?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3">Shortlist examined</p>
              <div className="flex flex-wrap gap-2">
                {res.shortlist.map((s, i) => (
                  <span
                    key={s.spelling}
                    data-testid={`name-shortlist-${i}`}
                    className="text-sm text-neutral-200 border border-white/10 rounded-full px-3 py-1"
                  >
                    {s.spelling}
                  </span>
                ))}
              </div>
            </div>
          )}
        </ResultCard>
      )}
    </div>
  );
};

/* ---------- Number Analyzer ---------- */
const NumberForm = ({ kind }) => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", dob: "", number: "" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const digits = useMemo(() => (f.number || "").replace(/\D/g, ""), [f.number]);
  const canSubmit = f.full_name && f.dob && digits.length >= 6;

  const submit = async () => {
    if (!f.full_name || !f.dob) { toast.error("Enter your name and date of birth."); return; }
    if (digits.length < 6) { toast.error("Enter at least 6 digits."); return; }
    if ((user?.credits || 0) < 2) { toast.error("Number check costs 2 credits."); nav("/pricing"); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/numerology/number-check", { ...f, kind });
      setRes(data);
      await refresh();
      toast.success(`${kind === "mobile" ? "Mobile" : "Bank"} number analysed · 2 credits used`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
      if (e?.response?.status === HTTP_PAYMENT_REQUIRED) nav("/pricing");
    } finally { setLoading(false); }
  };

  const Icon = kind === "mobile" ? Phone : Landmark;

  return (
    <div>
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Full name <span className="text-gold">*</span></Label>
          <Input
            data-testid={`${kind}-name`}
            value={f.full_name}
            onChange={(e) => setF({ ...f, full_name: e.target.value })}
            placeholder="Amara Blackwood"
            className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Date of birth <span className="text-gold">*</span></Label>
          <Input
            data-testid={`${kind}-dob`}
            type="date"
            value={f.dob}
            onChange={(e) => setF({ ...f, dob: e.target.value })}
            max={new Date().toISOString().slice(0, 10)}
            className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold text-neutral-200"
          />
        </div>
      </div>
      <div className="mt-5">
        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
          {kind === "mobile" ? "Mobile number" : "Bank account number"} <span className="text-gold">*</span>
        </Label>
        <Input
          data-testid={`${kind}-number`}
          value={f.number}
          onChange={(e) => setF({ ...f, number: e.target.value })}
          placeholder={kind === "mobile" ? "+1 555 123 4567" : "0123 4567 89"}
          inputMode="numeric"
          className="mt-2 bg-black/40 border-white/10 focus-visible:ring-gold font-mono tracking-wider"
        />
        <p className="text-xs text-neutral-500 mt-2">
          {digits.length > 0 ? `${digits.length} digits captured` : "Digits only, spaces/dashes are ignored"}
        </p>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-neutral-400">Cost: <span className="text-gold">2 credits</span></span>
        <Button
          data-testid={`${kind}-submit`}
          onClick={submit}
          disabled={loading || !canSubmit}
          className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-8 transition-colors duration-200 disabled:opacity-50"
        >
          <Icon className="h-4 w-4 mr-2" /> {loading ? "Checking…" : "Analyse & suggest"}
        </Button>
      </div>

      {loading && <Loader />}
      {res && (
        <ResultCard score={res.score} verdict={res.verdict} testid={`${kind}-result`}>
          {renderAnswer(res.reading.answer)}
          {res.suggestions?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Favourable alternatives</p>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                {res.suggestions.map((s, i) => (
                  <div
                    key={s.full || s.pattern}
                    data-testid={`${kind}-suggestion-${i}`}
                    className="rounded-xl border border-gold/20 bg-black/40 p-4"
                  >
                    <div className="font-mono text-lg tracking-wider text-gold">
                      {s.full || s.pattern}
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">
                      {s.rating}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                Adjust the last two digits of your existing {kind === "mobile" ? "number" : "account"} to any pattern above for a friendlier vibration.
              </p>
            </div>
          )}
        </ResultCard>
      )}
    </div>
  );
};

/* ---------- Page ---------- */
const Tools = () => {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
      <div className="mb-8 sm:mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Advisors</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter">Tools</h1>
        <p className="text-neutral-400 mt-3 max-w-xl text-sm sm:text-base">
          Check compatibility with a partner, refine your name's vibration, or discover which
          mobile and bank numbers move with you rather than against you.
        </p>
      </div>

      <Tabs defaultValue="compat" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-[#141419] border border-white/5 p-1 h-auto gap-1">
          <TabsTrigger value="compat" data-testid="tab-compat" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-full py-2 text-xs sm:text-sm">
            Compatibility
          </TabsTrigger>
          <TabsTrigger value="name" data-testid="tab-name" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-full py-2 text-xs sm:text-sm">
            Name Advisor
          </TabsTrigger>
          <TabsTrigger value="mobile" data-testid="tab-mobile" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-full py-2 text-xs sm:text-sm">
            Mobile
          </TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-full py-2 text-xs sm:text-sm">
            Bank
          </TabsTrigger>
        </TabsList>

        <div className="mt-6 sm:mt-8 rounded-2xl border border-white/5 bg-[#141419] p-5 sm:p-8">
          <TabsContent value="compat" className="mt-0">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <h2 className="font-serif text-2xl">Two-person compatibility</h2>
              </div>
              <p className="text-sm text-neutral-400">Weigh a partner, friend, or business match against your own vibration.</p>
            </div>
            <CompatibilityForm />
          </TabsContent>
          <TabsContent value="name" className="mt-0">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <h2 className="font-serif text-2xl">Name spelling advisor</h2>
              </div>
              <p className="text-sm text-neutral-400">See which spelling of your name best supports your goal.</p>
            </div>
            <NameAdvisorForm />
          </TabsContent>
          <TabsContent value="mobile" className="mt-0">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <h2 className="font-serif text-2xl">Mobile number check</h2>
              </div>
              <p className="text-sm text-neutral-400">Discover if your phone number is working with you — and see favourable alternatives.</p>
            </div>
            <NumberForm kind="mobile" />
          </TabsContent>
          <TabsContent value="bank" className="mt-0">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <h2 className="font-serif text-2xl">Bank account check</h2>
              </div>
              <p className="text-sm text-neutral-400">Test the vibration of your bank account number and find friendlier variants.</p>
            </div>
            <NumberForm kind="bank" />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Tools;
